import { axiosInstance } from "../services/v2/axiosInstance";

/**
 * Cliente de wa-mail (el servicio de correo de WappMail, detrás de nginx /mail-api/). Va con el mismo token de WAPP:
 * wa-mail se lo pregunta a WAPP. Lo que no es correo (¿tengo buzón?, contactos del personal) lo contesta WAPP mismo.
 */
export const MAIL_URL: string = (process.env.REACT_APP_MAIL_URL || (() => {
	try { return new URL(process.env.REACT_APP_API_URL_V2 || "").origin + "/mail-api"; } catch { return ""; }
})()).replace(/\/$/, "");

export interface Dir { nombre: string; email: string }
export interface Carpeta { ruta: string; nombre: string; especial: string | null; total: number | null; no_leidos: number | null; delim: string }
export interface Resumen {
	uid: number; de: Dir | null; para: Dir[]; asunto: string; fecha: string | null;
	leido: boolean; marcado: boolean; respondido: boolean; adjuntos: boolean; tam: number;
}
export interface Pagina { total: number; pagina: number; por: number; mensajes: Resumen[] }
export interface AdjuntoInfo { parte: number; nombre: string; tipo: string; tam: number; cid: string | null }
export interface Mensaje {
	uid: number; carpeta: string; asunto: string; fecha: string | null; de: Dir | null; para: Dir[]; cc: Dir[]; responder_a: Dir[];
	html: string; imagenes_bloqueadas: number; texto: string; adjuntos: AdjuntoInfo[]; marcado: boolean; yo: string;
}
export interface MiBuzon {
	user: { id: number; nombre: string; email: string };
	buzon: { id: number; email: string; estado: string; con_clave: boolean } | null;
	firma: { html: string; texto: string } | null;
}
export interface Contacto { nombre: string; email: string; tipo: "persona" | "lista" }
export interface DatosEnvio {
	para: Dir[]; cc: Dir[]; cco: Dir[]; asunto: string; html: string; firma: boolean;
	responder?: { carpeta: string; uid: number }; reenviar?: { carpeta: string; uid: number };
}

const M = (ruta: string) => MAIL_URL + ruta;
const cfg = (params?: any) => ({ params });

export const mailApi = {
	/** WAPP (no wa-mail): ¿tengo buzón activo? y mi firma. */
	yo: () => axiosInstance.get<MiBuzon>("/correo/yo"),
	contactos: (q: string) => axiosInstance.get<{ contactos: Contacto[] }>("/correo/contactos", { params: { q } }),

	carpetas: () => axiosInstance.get<{ carpetas: Carpeta[] }>(M("/carpetas"), { baseURL: "" }),
	noLeidos: () => axiosInstance.get<{ no_leidos: number }>(M("/no-leidos"), { baseURL: "" }),
	mensajes: (carpeta: string, pagina: number) => axiosInstance.get<Pagina>(M("/mensajes"), { baseURL: "", ...cfg({ carpeta, pagina, por: 50 }) }),
	buscar: (carpeta: string, q: string, pagina: number) => axiosInstance.get<Pagina>(M("/buscar"), { baseURL: "", ...cfg({ carpeta, q, pagina }) }),
	mensaje: (carpeta: string, uid: number, imagenes: boolean) =>
		axiosInstance.get<Mensaje>(M("/mensaje"), { baseURL: "", ...cfg({ carpeta, uid, imagenes: imagenes ? 1 : 0 }) }),
	adjunto: (carpeta: string, uid: number, parte: number) =>
		axiosInstance.get<Blob>(M("/adjunto"), { baseURL: "", responseType: "blob", ...cfg({ carpeta, uid, parte }) }),
	marcar: (carpeta: string, uids: number[], bandera: "leido" | "marcado", valor: boolean) =>
		axiosInstance.post(M("/marcar"), { carpeta, uids, bandera, valor }, { baseURL: "" }),
	mover: (carpeta: string, uids: number[], destino: string) => axiosInstance.post(M("/mover"), { carpeta, uids, destino }, { baseURL: "" }),
	borrar: (carpeta: string, uids: number[]) => axiosInstance.post<{ definitivo: boolean }>(M("/borrar"), { carpeta, uids }, { baseURL: "" }),
	enviar: (datos: DatosEnvio, archivos: File[]) => {
		const f = new FormData();
		f.append("datos", JSON.stringify(datos)); // como texto: un Blob llegaría como archivo ("blob")
		archivos.forEach((a) => f.append("archivo", a, a.name));
		return axiosInstance.post<{ ok: boolean; guardado_en_enviados: boolean }>(M("/enviar"), f, { baseURL: "" });
	},
};

export const errorCorreo = (e: any): string => {
	if (!e?.response && /Network Error|timeout/i.test(String(e?.message))) return "No se pudo conectar con el correo. Intenta otra vez en un momento.";
	return e?.response?.data?.message || e?.message || "Algo falló. Intenta otra vez.";
};

const MESES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
const hora12 = (d: Date) => { const h = d.getHours(); return (h % 12 || 12) + ":" + String(d.getMinutes()).padStart(2, "0") + " " + (h < 12 ? "am" : "pm"); };

/** Fecha corta para la lista: hoy = "2:24 pm"; este año = "10 oct"; antes = "10 oct 2025". */
export const fechaCorta = (iso: string | null): string => {
	if (!iso) return "";
	const d = new Date(iso);
	if (isNaN(d.getTime())) return "";
	const hoy = new Date();
	if (d.toDateString() === hoy.toDateString()) return hora12(d);
	return d.getDate() + " " + MESES[d.getMonth()] + (d.getFullYear() !== hoy.getFullYear() ? " " + d.getFullYear() : "");
};
export const fechaLarga = (iso: string | null): string => {
	if (!iso) return "";
	const d = new Date(iso);
	return isNaN(d.getTime()) ? "" : d.getDate() + " " + MESES[d.getMonth()] + " " + d.getFullYear() + ", " + hora12(d);
};
export const tamano = (n: number): string => (n < 1024 ? n + " B" : n < 1048576 ? Math.round(n / 1024) + " KB" : (n / 1048576).toFixed(1) + " MB");
export const nombreDe = (d: Dir | null): string => (d ? d.nombre || d.email : "(sin remitente)");

// ------------------------------------------------------------------ ajustes del buzón (Fuera de Oficina, Reenvío, Filtros): los guarda WAPP
export interface FueraOficina { activo: boolean; asunto?: string; cuerpo?: string; desde?: string | null; hasta?: string | null }
export interface Reenvio { activo: boolean; a?: string; copia?: boolean; hasta?: string | null }
export interface FiltroCorreo {
	campo: "de" | "para" | "asunto" | "cuerpo"; op: "contiene" | "es"; valor: string;
	accion: "mover" | "leido" | "destacar" | "eliminar"; carpeta?: string; leido?: boolean;
}
export interface AjustesBuzon {
	email: string; estado: string; fuera: FueraOficina; reenvio: Reenvio; filtros: FiltroCorreo[];
	reenvio_externo: { confirmado: boolean; bloqueado: boolean } | null; ocupado_por: string | null; sieve_error: string | null;
}
const rutaAjustes = (buzonId?: number) => (buzonId ? "/correo/buzones/" + buzonId + "/_ajustes" : "/correo/yo/_ajustes");
export const ajustesApi = {
	leer: (buzonId?: number) => axiosInstance.get<AjustesBuzon>(rutaAjustes(buzonId)),
	guardar: (parte: Partial<Pick<AjustesBuzon, "fuera" | "reenvio" | "filtros">> & { forzar?: boolean }, buzonId?: number) =>
		axiosInstance.put<AjustesBuzon>(rutaAjustes(buzonId), parte),
};
